// Lib
import { reduce, add, get } from 'lodash';
import { uniqBy } from 'lodash/fp';

// Element Registry
import { getElementDisplayName, getElementDisplayNamePlural } from '../elements/elementRegistry';

// Utils
import { convertElementCountMapForRender, reduceTypeCounts } from '../elements/elementCountsUtils';
import { pluralise, listSeparator, chooseSingular } from '../utils/stringUtil';

// Constants
import { MODIFIED_ELEMENTS_BOARD_MESSAGES, MODIFIED_ELEMENTS_MESSAGES } from './notificationConstants';
import { ElementType } from '../elements/elementTypes';

const uniqById = uniqBy('_id');

// ----------------------
// Describe a map of { BOARD: 2, CARD: 1, LINK: 1 }
// as "2 boards, 1 note and 1 link"
// ----------------------
export const describeElementTypes = (elementTypeChanges) => {
    const typesArray = Object.keys(elementTypeChanges).sort();

    return typesArray
        .filter((type) => elementTypeChanges[type] > 0 && !!getElementDisplayName(type))
        .reduce((acc, type, i, self) => {
            const count = elementTypeChanges[type];
            const typeName = getElementDisplayName(type).toLowerCase();

            const name = chooseSingular(
                count,
                typeName,
                getElementDisplayNamePlural(type)?.toLowerCase() || pluralise(typeName, count),
            );

            const separator = listSeparator(i, self.length);
            return `${acc}${count} ${name}${separator}`;
        }, '');
};

export const describeContentCounts = (elementContentCounts) => {
    const typesArray = Object.keys(elementContentCounts).sort();

    return typesArray.reduce((acc, type, i, self) => {
        const count = elementContentCounts[type];
        const typeName = type.toLowerCase();
        const name = pluralise(typeName, count);
        const separator = listSeparator(i, self.length);
        return `${acc}${count} ${name}${separator}`;
    }, '');
};

// ----------------------
// Turn an array of [{ elementType: 'CARD', message: 'CREATED' }, { elementType: 'BOARD', message: 'UPDATED' }, { elementType: 'CARD', message: 'UPDATED' }]
// into a map of { created: { CARD: 1 }, updated: { BOARD: 1, CARD: 1 } }
// ----------------------
export const parseChangedElementTypes = (changes) => {
    const newElements = convertElementCountMapForRender(
        changes.filter((change) => change.message === MODIFIED_ELEMENTS_MESSAGES.CREATED).reduce(reduceTypeCounts, {}),
    );

    const updatedElements = convertElementCountMapForRender(
        changes.filter((change) => change.message === MODIFIED_ELEMENTS_MESSAGES.UPDATED).reduce(reduceTypeCounts, {}),
    );

    return {
        created: newElements,
        updated: updatedElements,
    };
};

const getTotalChangeCount = (changesData) => reduce(changesData.created, add, 0) + reduce(changesData.updated, add, 0);

export const buildChangesMessage = ({ commentChangesCount, otherChangesCount }) => {
    let str = ' ';
    if (otherChangesCount) {
        str = ` made ${otherChangesCount} ${pluralise('change', otherChangesCount)}`;
    }

    if (commentChangesCount) {
        str +=
            `${otherChangesCount ? ' and ' : ''}added ` +
            `${commentChangesCount} ${pluralise('comment', commentChangesCount)}`;
    }

    return str;
};

// ----------------------
// Describe a map of { created: { CARD: 1, COMMENT_THREAD: 2 }, updated: { BOARD: 1, CARD: 1 } }
// as "made 3 changes and added 2 comments"
// ----------------------
export const getChangesMessageFromSummary = (changesData) => {
    const totalChanges = getTotalChangeCount(changesData);
    const commentChangesCount = get(changesData, ['created', ElementType.COMMENT_THREAD_TYPE]) || 0;
    const otherChangesCount = totalChanges - commentChangesCount;

    return buildChangesMessage({ commentChangesCount, otherChangesCount });
};

// ----------------------
// Describe a map of { created: { CARD: 1, COMMENT_THREAD: 2 }, updated: { BOARD: 1, CARD: 1 } }
// as "made 5 changes"
// ----------------------
export const describeChangedElements = (changesData) => {
    const totalChanges = getTotalChangeCount(changesData);
    return `made ${totalChanges} ${pluralise('change', totalChanges)}`;
};

// ----------------------
// Returns the users that have made changes.
// ----------------------
export const parseChangingUsers = (changes, users) =>
    changes
        .map((change) => change.modifiedBy)
        .filter((userId, i, self) => self.indexOf(userId) === i)
        .map((userId) => users[userId]);

// ----------------------
// Describe an array of [{ modifiedBy: 'abc' }, { modifiedBy: 'xyz' }, { modifiedBy: 'abc' }, { modifiedBy: 'jkl' }]
// as "Isaac Williams, Glenn Allen and Ollie Campbell"
// ----------------------
export const describeChangingUsers = (changes, users) =>
    uniqById(parseChangingUsers(changes, users))
        .map((user) => user.name.displayName)
        .reduce((acc, name, i, self) => acc + name + listSeparator(i, self.length), '');

export const getAllModifiedChildren = (changedBoards) =>
    changedBoards.reduce((acc, board) => acc.concat(board.children), []);

export const getAllChangedElements = (changedBoards) =>
    changedBoards
        .filter((change) => change.message !== MODIFIED_ELEMENTS_BOARD_MESSAGES.VIEWED)
        .reduce((acc, board) => acc.concat(board.children), []);
